/**
 * Admin Users Page
 *
 * Displays aggregated user statistics for admin users.
 * Uses TanStack Table for sorting and filtering with Mantine UI components.
 */
import { useMemo, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Table,
  Card,
  Group,
  Badge,
  Loader,
  Alert,
  TextInput,
  Stack,
} from '@mantine/core';
import { IconAlertCircle, IconSearch } from '@tabler/icons-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { fetchAdminUsersStats } from '../api/admin';
import type { UserStats } from '../types/admin';

const columnHelper = createColumnHelper<UserStats>();

export function AdminUsersPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users-stats'],
    queryFn: fetchAdminUsersStats,
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('display_name', {
        header: 'Display Name',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('tank_count', {
        header: 'Tanks',
        cell: (info) => (
          <Badge color="blue" variant="light">
            {info.getValue()}
          </Badge>
        ),
      }),
      columnHelper.accessor('device_count', {
        header: 'Devices',
        cell: (info) => (
          <Badge color="green" variant="light">
            {info.getValue()}
          </Badge>
        ),
      }),
      columnHelper.accessor('measurement_count', {
        header: 'Measurements',
        cell: (info) => (
          <Badge color="orange" variant="light">
            {info.getValue()}
          </Badge>
        ),
      }),
      columnHelper.accessor('dosing_count', {
        header: 'Dosing Records',
        cell: (info) => (
          <Badge color="grape" variant="light">
            {info.getValue()}
          </Badge>
        ),
      }),
      columnHelper.accessor('created_at', {
        header: 'Joined',
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
      }),
      columnHelper.accessor('last_login', {
        header: 'Last Login',
        cell: (info) => {
          const value = info.getValue();
          return value ? new Date(value).toLocaleDateString() : 'Never';
        },
      }),
    ],
    []
  );

  const table = useReactTable({
    data: data?.users ?? [],
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error instanceof Error ? error.message : 'Failed to load user statistics'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>User Statistics</Title>
            <Text c="dimmed" size="sm">
              {data?.total_users ?? 0} total users
            </Text>
          </div>
        </Group>

        <TextInput
          placeholder="Search users..."
          leftSection={<IconSearch size={16} />}
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.currentTarget.value)}
        />

        <Card withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.Th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ cursor: 'pointer' }}
                    >
                      <Group gap="xs">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' ↑',
                          desc: ' ↓',
                        }[header.column.getIsSorted() as string] ?? null}
                      </Group>
                    </Table.Th>
                  ))}
                </Table.Tr>
              ))}
            </Table.Thead>
            <Table.Tbody>
              {table.getRowModel().rows.map((row) => (
                <Table.Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      </Stack>
    </Container>
  );
}
